/**
 * The Bacteria. Level 0's own, and the thing that runs.
 *
 * It has one idea and it is the documented one: **the longer it chases, the
 * faster it gets**. Everything else about it is arranged to make that legible.
 * It sees and it hears, so unlike the hound there is no standing still your way
 * out of one that has already looked at you. It screams when it commits, which
 * is the only warning and the only sound it ever makes — the lore is specific
 * that it moves quietly, and a thing crossing a lit yellow room in silence at
 * increasing speed is worse than one announcing every footfall.
 *
 * The counterplay is temporal rather than positional. At the moment it commits
 * it is slower than a sprint, so the first corner is genuinely worth taking; by
 * twenty seconds it is comfortably faster, so the fifth is not. The answer is
 * to break line of sight *early*, while it is still slow, and then go quiet —
 * or, on a floor with a slide on it, to already know where the slide is.
 */

import { clamp01 } from '../../core/math';
import { TUNING } from '../../core/config';
import {
  driveAwareness,
  faceToward,
  holdStill,
  moveTo,
  setMode,
  sweepStep,
  takeHeardNoise,
  tryAttack,
  type Agent,
  type Behavior,
  type EntityWorld,
} from '../brain';
import { ENTITY_TUNING } from '../defs';
import { hearSignal, sightSignal } from '../perception';
import { VOICE } from '../voice';

const T = ENTITY_TUNING.bacteria;

/**
 * The ramp, kept on `Agent.counter` so it survives mode changes.
 *
 * It is a fraction 0..1 of the way to `rampPeak`, climbing while the thing is
 * actually chasing and bleeding off slowly when it is not. The slow bleed
 * matters: a player who breaks line of sight for four seconds and is then found
 * again should be picked up by something that has barely calmed down, or
 * dodging round a pillar would reset the entity's one mechanic.
 */
function ramp(a: Agent, w: EntityWorld, chasing: boolean): number {
  if (chasing) a.counter = clamp01(a.counter + w.dt / T.rampTime);
  else a.counter = Math.max(0, a.counter - T.rampCool * w.dt);
  return 1 + (T.rampPeak - 1) * a.counter;
}

/** Point its memory at a world position, allocating it once. */
function remember(a: Agent, x: number, z: number): void {
  const s = a.state;
  if (!s.lastKnown) s.lastKnown = { x, z };
  else {
    s.lastKnown.x = x;
    s.lastKnown.z = z;
  }
}

export const bacteriaBehavior: Behavior = {
  kind: 'bacteria',

  spawn(a) {
    // It turns quickly. Nothing about it is stiff except the way it looks.
    a.turnRate = 5.0;
    a.counter = 0;
    a.timer = 0;
    a.timer2 = 0;
  },

  think(a, w) {
    const s = a.state;

    const signal = Math.max(
      sightSignal(a.def, s, w.grid, w.player, w.playerLight),
      hearSignal(a.def, s, w.grid, w.player),
    );
    driveAwareness(a, w, signal, T.gain, T.forget);

    const spot = takeHeardNoise(a);
    if (spot) {
      remember(a, spot.x, spot.z);
      s.awareness = Math.min(1, s.awareness + a.heardLoudness * 0.5);
      a.searchTimer = TUNING.entities.searchDuration;
    }

    const chasing = s.mode === 'chase' || s.mode === 'attack';
    const speed = a.def.chaseSpeed * ramp(a, w, chasing);

    switch (s.mode) {
      case 'chase': {
        const target = s.lastKnown ?? w.player.pos;
        moveTo(a, w, target, speed);

        // It screams while it runs, and the scream gets lower as it gets
        // faster. That is the only cue the player has for the acceleration, so
        // it is the one thing it is loud about.
        a.timer -= w.dt;
        if (a.timer <= 0) {
          a.timer = T.screamInterval;
          w.audio.play(VOICE.bacteriaScream, {
            pos: s.pos,
            gain: 0.9,
            rate: 1.05 - 0.35 * a.counter,
          });
        }

        if (s.distToPlayer <= a.def.attackRange) {
          setMode(a, w, 'attack');
          break;
        }
        // It commits hard, then overshoots to the last thing it had.
        if (s.modeTime > T.chaseCommit && s.awareness < T.dropAwareness) setMode(a, w, 'investigate');
        break;
      }

      case 'attack': {
        faceToward(a, w.player.pos);
        holdStill(a);
        if (tryAttack(a, w)) {
          s.awareness = 1;
          remember(a, w.player.pos.x, w.player.pos.z);
        }
        if (s.distToPlayer > a.def.attackRange * 1.6) setMode(a, w, 'chase');
        break;
      }

      case 'investigate': {
        if (!s.lastKnown) {
          setMode(a, w, 'wander');
          break;
        }
        if (s.awareness >= T.commitAwareness) {
          // The commit. One scream, at full volume, and then it is running.
          w.audio.play(VOICE.bacteriaScream, { pos: s.pos, gain: 1, rate: 0.95 });
          a.timer = T.screamInterval;
          setMode(a, w, 'chase');
          break;
        }
        moveTo(a, w, s.lastKnown, a.def.walkSpeed * 1.6);
        if (a.searchTimer <= 0) {
          s.lastKnown = null;
          s.awareness = 0;
          setMode(a, w, 'wander');
        }
        break;
      }

      default: {
        setMode(a, w, 'wander');
        sweepStep(a, w, a.def.walkSpeed);
        if (s.awareness > 0.15 && s.lastKnown) setMode(a, w, 'investigate');
        break;
      }
    }
  },
};
